// src/components/ProductModal.js
import React, { useState, useEffect, useRef } from 'react';

const ProductModal = ({ 
  product, 
  onClose, 
  primaryColor, 
  secondaryColor, 
  contacts, 
  categories,
  language = 'en' // 'en' or 'km'
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const [touchStart, setTouchStart] = useState(0);
  const imageContainerRef = useRef(null);
  const imageRef = useRef(null);

  // Translations
  const translations = {
    en: {
      noImage: 'No Image',
      noDescription: 'No description available for this product.',
      zoomed: 'Zoomed',
      productDescription: 'Product Description',
      contactSeller: 'Contact Seller',
      typicallyReplies: 'Typically replies within 24 hours',
      contactSellerOn: 'Contact Seller on',
      orChooseFrom: 'Or choose from',
      otherPlatforms: 'other platforms above',
      productInfo: 'Product Information',
      price: 'Price',
      discount: 'Discount',
      category: 'Category',
      viewProduct: 'View Product',
      sharedOn: 'Shared on',
      productInquiry: 'Product Inquiry',
      interestedIn: "I'm interested in your product:",
      pleaseProvide: 'Please provide more information.',
      addToCart: 'Add to Cart',
      addedToCart: 'Added to Cart!',
      inStock: 'In Stock',
      outOfStock: 'Out of Stock',
      quantity: 'Quantity',
      total: 'Total'
    },
    km: {
      noImage: 'គ្មានរូបភាព',
      noDescription: 'គ្មានការពិពណ៌នាសម្រាប់ផលិតផលនេះទេ។',
      zoomed: 'បានពង្រីក',
      productDescription: 'ការពិពណ៌នាផលិតផល',
      contactSeller: 'ទាក់ទងអ្នកលក់',
      typicallyReplies: 'ជាធម្មតាឆ្លើយតបក្នុងរយៈពេល ២៤ ម៉ោង',
      contactSellerOn: 'ទាក់ទងអ្នកលក់តាមរយៈ',
      orChooseFrom: 'ឬជ្រើសរើសពី',
      otherPlatforms: 'បណ្តាញផ្សេងទៀតខាងលើ',
      productInfo: 'ព័ត៌មានផលិតផល',
      price: 'តម្លៃ',
      discount: 'បញ្ចុះតម្លៃ',
      category: 'ប្រភេទ',
      viewProduct: 'មើលផលិតផល',
      sharedOn: 'ចែករំលែកនៅថ្ងៃ',
      productInquiry: 'សំណួរអំពីផលិតផល',
      interestedIn: 'ខ្ញុំចាប់អារម្មណ៍លើផលិតផលរបស់អ្នក៖',
      pleaseProvide: 'សូមផ្តល់ព័ត៌មានបន្ថែម។',
      addToCart: 'បន្ថែមទៅកន្ត្រក',
      addedToCart: 'បានបន្ថែមទៅកន្ត្រក!',
      inStock: 'មានស្តុក',
      outOfStock: 'អស់ស្តុក',
      quantity: 'ចំនួន',
      total: 'សរុប'
    }
  };

  const t = translations[language] || translations.en;

  // Get font family based on language
  const getFontFamily = () => {
    if (language === 'km') {
      return "'Kantumruy Pro', 'Noto Sans Khmer', 'Noto Serif Khmer', 'Khmer OS', sans-serif";
    }
    return "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  };

  // Get images with safety check
  const getImages = () => {
    try {
      if (!product) return ['https://via.placeholder.com/800x800?text=No+Image'];
      
      const productImages = product.images;
      if (productImages && Array.isArray(productImages) && productImages.length > 0) {
        return productImages.map(img => {
          if (!img) return 'https://via.placeholder.com/800x800?text=No+Image';
          if (img.startsWith('http')) return img;
          if (img.startsWith('data:')) return img;
          return `https://easymenu-backend-4w2d.onrender.com${img}`;
        });
      }
      return ['https://via.placeholder.com/800x800?text=No+Image'];
    } catch (error) {
      console.error('Error loading images:', error);
      return ['https://via.placeholder.com/800x800?text=No+Image'];
    }
  };

  const images = getImages();

  // Find category name from category ID
  const getCategoryName = () => {
    try {
      if (!product?.category_id || !categories || categories.length === 0) {
        return null;
      }
      const category = categories.find(cat => cat.id === product.category_id);
      return category ? category.title : null;
    } catch (error) {
      return null;
    }
  };

  // Format product information for sharing
  const getProductInfoText = () => {
    try {
      const price = parseFloat(product?.price) || 0;
      const discountPrice = parseFloat(product?.discount_price) || 0;
      const discount = discountPrice > 0 && discountPrice < price 
        ? Math.round(((price - discountPrice) / price) * 100) 
        : 0;
      
      const categoryName = getCategoryName();
      
      let infoText = `🛍 *${t.productInfo}: ${product?.title || 'Product'}*\n\n`;
      
      if (categoryName) {
        infoText += `📁 *${t.category}:* ${categoryName}\n`;
      }
      
      if (discountPrice > 0 && discountPrice < price) {
        infoText += `💰 *${t.price}:* ~~$${price.toFixed(2)}~~ → $${discountPrice.toFixed(2)}\n`;
        infoText += `🎉 *${t.discount}:* ${discount}% OFF\n`;
      } else {
        infoText += `💰 *${t.price}:* $${price.toFixed(2)}\n`;
      }
      
      if (product?.description) {
        infoText += `\n📝 *${t.productDescription}:*\n${product.description.substring(0, 200)}${product.description.length > 200 ? '...' : ''}\n`;
      }
      
      const currentImageUrl = images[currentImageIndex];
      if (currentImageUrl && !currentImageUrl.includes('placeholder')) {
        infoText += `\n🖼 *${t.viewProduct} Image:* ${currentImageUrl}\n`;
      }
      
      infoText += `\n🔗 *${t.viewProduct}:* ${window.location.href}\n`;
      infoText += `\n📅 *${t.sharedOn}:* ${new Date().toLocaleString()}`;
      
      return encodeURIComponent(infoText);
    } catch (error) {
      console.error('Error formatting product info:', error);
      return encodeURIComponent(`🛍 Product: ${product?.title || 'Product'}`);
    }
  };

  // Generate Telegram share link with product info
  const getTelegramLink = (contactLink) => {
    try {
      const productInfo = getProductInfoText();
      let telegramUsername = contactLink;
      
      if (contactLink.includes('t.me/')) {
        telegramUsername = contactLink.split('t.me/')[1];
      } else if (contactLink.includes('telegram.me/')) {
        telegramUsername = contactLink.split('telegram.me/')[1];
      } else if (contactLink.startsWith('@')) {
        telegramUsername = contactLink.substring(1);
      }
      
      telegramUsername = telegramUsername.split('?')[0];
      return `https://t.me/${telegramUsername}?text=${productInfo}`;
    } catch (error) {
      console.error('Error generating Telegram link:', error);
      return contactLink;
    }
  };

  // Generate WhatsApp share link
  const getWhatsAppLink = (contactLink) => {
    try {
      const productInfo = decodeURIComponent(getProductInfoText());
      const formattedInfo = encodeURIComponent(productInfo);
      
      let phoneNumber = contactLink.replace(/[^0-9+]/g, '');
      if (phoneNumber.startsWith('+')) {
        phoneNumber = phoneNumber.substring(1);
      }
      
      return `https://wa.me/${phoneNumber}?text=${formattedInfo}`;
    } catch (error) {
      console.error('Error generating WhatsApp link:', error);
      return contactLink;
    }
  };

  // Handle contact click with dynamic product info
  const handleContactClick = (contact) => {
    try {
      if (!contact) return;
      
      let link = contact.link || '';
      
      if (contact.platform?.toLowerCase() === 'telegram') {
        link = getTelegramLink(contact.link);
      } else if (contact.platform?.toLowerCase() === 'whatsapp') {
        link = getWhatsAppLink(contact.link);
      } else if (contact.platform?.toLowerCase() === 'email') {
        const subject = encodeURIComponent(`${t.productInquiry}: ${product?.title || 'Product'}`);
        const body = encodeURIComponent(
          `${t.interestedIn}\n\n` +
          `Product: ${product?.title || 'Product'}\n` +
          `Price: $${parseFloat(product?.discount_price || product?.price || 0).toFixed(2)}\n` +
          `Description: ${product?.description || t.noDescription}\n\n` +
          `${t.pleaseProvide}`
        );
        link = `mailto:${contact.link}?subject=${subject}&body=${body}`;
      }
      
      window.open(link, '_blank', 'noopener noreferrer');
    } catch (error) {
      console.error('Error handling contact click:', error);
      if (contact?.link) {
        window.open(contact.link, '_blank', 'noopener noreferrer');
      }
    }
  };

  const getIconClass = (platform) => {
    const icons = {
      facebook: 'fab fa-facebook',
      telegram: 'fab fa-telegram',
      phone: 'fas fa-phone',
      tiktok: 'fab fa-tiktok',
      instagram: 'fab fa-instagram',
      whatsapp: 'fab fa-whatsapp',
      twitter: 'fab fa-twitter',
      youtube: 'fab fa-youtube',
      messenger: 'fab fa-facebook-messenger',
      line: 'fab fa-line',
      wechat: 'fab fa-weixin',
      viber: 'fab fa-viber',
      email: 'fas fa-envelope',
      shopee: 'fas fa-store',
      lazada: 'fas fa-shopping-cart'
    };
    return icons[platform?.toLowerCase()] || 'fas fa-link';
  };

  // Image navigation functions
  const nextImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
      setIsZoomed(false);
    }
  };

  const prevImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
      setIsZoomed(false);
    }
  };

  const handleMouseMove = (e) => {
    if (!isZoomed || !imageContainerRef.current) return;
    
    const { left, top, width, height } = imageContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    
    setZoomPosition({
      x: Math.min(Math.max(x, 0), 100),
      y: Math.min(Math.max(y, 0), 100)
    });
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setTouchStart(e.touches[0].clientX);
    }
  };

  const handleTouchMove = (e) => {
    if (!isZoomed && e.touches.length === 1) {
      const touchEnd = e.touches[0].clientX;
      if (touchStart - touchEnd > 50) {
        nextImage();
        setTouchStart(0);
      }
      if (touchStart - touchEnd < -50) {
        prevImage();
        setTouchStart(0);
      }
    }
  };

  const toggleZoom = () => {
    setIsZoomed(!isZoomed);
    setZoomPosition({ x: 50, y: 50 });
  };

  // useEffect Hook - MUST be called before any conditional returns
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      } else if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'z' || e.key === 'Z') {
        setIsZoomed(!isZoomed);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentImageIndex, isZoomed]);

  // Safety check - AFTER all hooks have been called
  if (!product) {
    return null;
  }

  const price = parseFloat(product.price) || 0;
  const discountPrice = parseFloat(product.discount_price) || 0;
  const discount = discountPrice > 0 && discountPrice < price 
    ? Math.round(((price - discountPrice) / price) * 100) 
    : 0;
  const hasDiscount = discountPrice > 0 && discountPrice < price;
  const categoryName = getCategoryName();

  // Get primary contact for main CTA button
  const primaryContact = contacts && contacts.length > 0 ? contacts[0] : null;
  const isPrimaryTelegram = primaryContact?.platform?.toLowerCase() === 'telegram';

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-2 md:p-4"
      onClick={onClose}
      style={{ fontFamily: getFontFamily() }}
    >
      <div 
        className="bg-white w-full max-w-2xl max-h-[95vh] overflow-y-auto relative rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: getFontFamily() }}
      >
        {/* Fixed Close Button - Always visible */}
        <button
          onClick={onClose}
          className="fixed top-3 right-3 md:top-4 md:right-4 text-gray-500 hover:text-gray-700 z-50 bg-white shadow-lg p-2 md:p-3 rounded-full transition-all duration-200 hover:shadow-xl active:scale-95"
          style={{ 
            position: 'fixed', 
            right: '0.75rem', 
            top: '0.75rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            fontFamily: getFontFamily()
          }}
        >
          <i className="fas fa-times text-lg md:text-xl"></i>
        </button>
        
        {/* Image Gallery Section - Square 1:1 */}
        <div className="bg-gray-50 p-2 md:p-4">
          <div 
            ref={imageContainerRef}
            className="relative bg-white overflow-hidden cursor-zoom-in mx-auto rounded-lg"
            style={{ 
              width: '100%',
              aspectRatio: '1 / 1',
              maxWidth: '100%'
            }}
            onClick={toggleZoom}
            onMouseMove={handleMouseMove}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={() => setTouchStart(0)}
          >
            <div className="relative w-full h-full overflow-hidden">
              <img 
                ref={imageRef}
                src={images[currentImageIndex] || 'https://via.placeholder.com/800x800?text=No+Image'} 
                alt={product.title || 'Product'}
                className={`absolute top-0 left-0 w-full h-full object-contain transition-transform duration-200 ${
                  isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'
                }`}
                style={{
                  transform: isZoomed ? 'scale(2.5)' : 'scale(1)',
                  transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`
                }}
                loading="lazy"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/800x800?text=No+Image';
                }}
              />
            </div>
            
            {/* Zoom Indicator */}
            {isZoomed && (
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                <i className="fas fa-search-minus mr-1"></i>
                {t.zoomed}
              </div>
            )}
            
            {/* Navigation Arrows */}
            {images.length > 1 && !isZoomed && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  className="absolute left-1 md:left-2 top-1/2 transform -translate-y-1/2 bg-white shadow-md p-1.5 md:p-2 rounded-full transition-all duration-200 hover:shadow-lg active:scale-95"
                >
                  <i className="fas fa-chevron-left text-gray-700 text-xs md:text-sm"></i>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  className="absolute right-1 md:right-2 top-1/2 transform -translate-y-1/2 bg-white shadow-md p-1.5 md:p-2 rounded-full transition-all duration-200 hover:shadow-lg active:scale-95"
                >
                  <i className="fas fa-chevron-right text-gray-700 text-xs md:text-sm"></i>
                </button>
              </>
            )}
          </div>
          
          {/* Thumbnail Gallery */}
          {images.length > 1 && (
            <>
              <div className="flex justify-center gap-1.5 md:gap-2 mt-3 md:mt-4 overflow-x-auto pb-2 px-1">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrentImageIndex(index);
                      setIsZoomed(false);
                    }}
                    className={`flex-shrink-0 border-2 rounded transition-all duration-200 ${
                      index === currentImageIndex 
                        ? 'border-opacity-100' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                    style={{ 
                      borderColor: index === currentImageIndex ? primaryColor : 'transparent',
                      width: '50px',
                      height: '50px',
                      aspectRatio: '1 / 1'
                    }}
                  >
                    <img 
                      src={image} 
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover rounded"
                    />
                  </button>
                ))}
              </div>
              <div className="text-center mt-2 md:mt-3">
                <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                  {currentImageIndex + 1} / {images.length}
                </span>
              </div>
            </>
          )}
        </div>
        
        {/* Product Details Section */}
        <div className="p-4 md:p-6" style={{ fontFamily: getFontFamily() }}>
          {/* Category Badge */}
          {categoryName && (
            <div className="mb-2 md:mb-3">
              <span 
                className="inline-block px-2 py-1 text-xs font-medium rounded-full"
                style={{ backgroundColor: `${primaryColor}20`, color: primaryColor, fontFamily: getFontFamily() }}
              >
                <i className="fas fa-tag mr-1 text-xs"></i>
                {categoryName}
              </span>
            </div>
          )}
          
          <h2 
            className="text-lg md:text-2xl font-semibold mb-2 tracking-tight text-gray-800"
            style={{ fontFamily: getFontFamily() }}
          >
            {product.title || 'Untitled Product'}
          </h2>
          
          <div className="flex items-center mb-3 md:mb-4">
            {hasDiscount ? (
              <>
                <span className="text-xl md:text-3xl font-bold" style={{ color: secondaryColor || primaryColor, fontFamily: getFontFamily() }}>
                  ${discountPrice.toFixed(2)}
                </span>
                <span className="text-gray-400 line-through ml-2 md:ml-3 text-sm md:text-lg">
                  ${price.toFixed(2)}
                </span>
                <span className="ml-2 md:ml-3 bg-red-500 text-white px-1.5 md:px-2 py-0.5 text-xs font-semibold rounded-full">
                  -{discount}%
                </span>
              </>
            ) : (
              <span className="text-xl md:text-3xl font-bold" style={{ color: primaryColor, fontFamily: getFontFamily() }}>
                ${price.toFixed(2)}
              </span>
            )}
          </div>
          
          <div className="mb-4 md:mb-6">
            <h3 
              className="text-sm md:text-base font-semibold mb-1 md:mb-2 text-gray-700 tracking-tight"
              style={{ fontFamily: getFontFamily() }}
            >
              {t.productDescription}
            </h3>
            <p className="text-gray-600 text-xs md:text-sm leading-relaxed" style={{ fontFamily: getFontFamily() }}>
              {product.description || t.noDescription}
            </p>
          </div>
          
          {/* Contact Section */}
          {contacts && contacts.length > 0 && (
            <>
              <div className="mb-4 md:mb-6 border-t border-gray-200 pt-4 md:pt-6">
                <h3 
                  className="text-sm md:text-base font-semibold mb-2 md:mb-3 text-gray-700 tracking-tight"
                  style={{ fontFamily: getFontFamily() }}
                >
                  <i className="fas fa-headset mr-2" style={{ color: primaryColor }}></i>
                  {t.contactSeller}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 md:gap-2">
                  {contacts.map((contact) => (
                    <button
                      key={contact.id}
                      onClick={() => handleContactClick(contact)}
                      className="flex items-center justify-center gap-1.5 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm rounded-lg transition-all duration-200 hover:shadow-md active:scale-95 cursor-pointer"
                      style={{ 
                        backgroundColor: primaryColor,
                        fontFamily: getFontFamily()
                      }}
                    >
                      <i className={`${getIconClass(contact.platform)} text-white text-xs md:text-sm`}></i>
                      <span className="text-white font-medium capitalize text-xs" style={{ fontFamily: getFontFamily() }}>
                        {contact.platform}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3 md:mt-4 flex items-center gap-1 md:gap-2" style={{ fontFamily: getFontFamily() }}>
                  <i className="fas fa-clock text-xs"></i>
                  <span>{t.typicallyReplies}</span>
                </p>
              </div>
              
              <div className="mt-3 md:mt-4">
                <button
                  onClick={() => handleContactClick(primaryContact)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 md:py-3 text-white font-medium rounded-lg transition-all duration-200 hover:opacity-90 text-sm tracking-wide active:scale-95 cursor-pointer"
                  style={{ 
                    backgroundColor: primaryColor,
                    fontFamily: getFontFamily()
                  }}
                >
                  <i className={`${getIconClass(primaryContact?.platform)} text-xs md:text-sm`}></i>
                  {t.contactSellerOn} {primaryContact?.platform}
                  {isPrimaryTelegram && (
                    <span className="text-xs ml-2">(with product info)</span>
                  )}
                </button>
                {contacts.length > 1 && (
                  <p className="text-center text-xs text-gray-500 mt-2 md:mt-3" style={{ fontFamily: getFontFamily() }}>
                    {t.orChooseFrom} {contacts.length} {t.otherPlatforms}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductModal;
